import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";

/**
 * BetaGateContext — wraps the entire app. Until the user passes the gate
 * (POST /api/beta/gate with a valid code+email pair), the app renders the
 * BetaGate screen instead of any route.
 *
 * The gate is server-validated (HttpOnly cookie). We expose a `betaEmail` and
 * `betaCode` so the watermark can read them.
 */
const BetaGateContext = createContext({
  loading: true,
  unlocked: false,
  celebrating: false,
  betaEmail: null,
  betaCode: null,
  unlock: async () => false,
  lock: async () => {},
  refresh: async () => {},
});

export function BetaGateProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [unlocked, setUnlocked] = useState(false);
  const [celebrating, setCelebrating] = useState(false);
  const [betaEmail, setBetaEmail] = useState(null);
  const [betaCode, setBetaCode] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/beta/check");
      if (data?.access) {
        setUnlocked(true);
        setBetaEmail(data.email || null);
        setBetaCode(data.code || null);
      } else {
        setUnlocked(false);
        setBetaEmail(null);
        setBetaCode(null);
      }
    } catch {
      setUnlocked(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const unlock = useCallback(async (code, email) => {
    // Server validates + sets cookie. Throws on invalid.
    const { data } = await api.post("/beta/gate", {
      code: code.trim().toUpperCase(),
      email: email.trim().toLowerCase(),
    });
    // Stash identity but DON'T flip unlocked yet — let the celebration play.
    setBetaEmail(data.email);
    setBetaCode(data.code);
    setCelebrating(true);
    // After the celebration, flip unlocked so the underlying app renders.
    await new Promise((r) => setTimeout(r, 2500));
    setUnlocked(true);
    setCelebrating(false);
    return true;
  }, []);

  const lock = useCallback(async () => {
    try { await api.post("/beta/logout"); } catch { /* ignore */ }
    setUnlocked(false);
    setCelebrating(false);
    setBetaEmail(null);
    setBetaCode(null);
  }, []);

  return (
    <BetaGateContext.Provider value={{ loading, unlocked, celebrating, betaEmail, betaCode, unlock, lock, refresh }}>
      {children}
    </BetaGateContext.Provider>
  );
}

export const useBetaGate = () => useContext(BetaGateContext);
